/**
 * Feedback — free-text + optional screenshot, posted to /feedback.
 * Auto-captures page URL (which page you were on when you opened this)
 * and conversation_id if you came from a /chat/{id} page (visible in
 * the form so you know what context is being sent).
 *
 * No surprise: the "context being sent" panel shows the user the
 * auto-captured fields before they submit.
 */

import { useRef, useState } from "react";

import { ICON_STROKE, NavIcons, X } from "@/shared/components/icons";
import { submitFeedback } from "@/shared/api";

const MAX_TEXT = 2000;
const MAX_SCREENSHOT_MB = 5;
const MAX_SCREENSHOT_BYTES = MAX_SCREENSHOT_MB * 1024 * 1024;

// Pulled from a referrer header where possible, else just the previous
// in-app URL we may have come from. The submit button always sends the
// current page (which IS /feedback) plus a recorded "from" URL we
// captured on mount.
function capturedContext(): { pageUrl: string; conversationId: string | null } {
  let pageUrl = document.referrer || window.location.href;
  // If the referrer is from a different origin (or empty) fall back to
  // window.location.href — friend test runs on app.schliengerllc.com
  // and referrer should always be same-origin, but be defensive.
  try {
    const refUrl = new URL(pageUrl);
    if (refUrl.origin !== window.location.origin) {
      pageUrl = window.location.href;
    }
  } catch {
    pageUrl = window.location.href;
  }
  let conversationId: string | null = null;
  const match = pageUrl.match(/\/chat\/([^/?#]+)/);
  if (match) conversationId = match[1];
  return { pageUrl, conversationId };
}

const FeedbackIcon = NavIcons.feedback;

export function Feedback() {
  const [text, setText] = useState("");
  const [screenshot, setScreenshot] = useState<File | null>(null);
  const [screenshotError, setScreenshotError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Snapshot once on mount so the context doesn't shift if the user
  // navigates within /feedback itself.
  const [context] = useState(capturedContext);

  function pickScreenshot(file: File | null) {
    setScreenshotError(null);
    if (!file) {
      setScreenshot(null);
      return;
    }
    if (!file.type.startsWith("image/")) {
      setScreenshotError("That doesn't look like an image file.");
      return;
    }
    if (file.size > MAX_SCREENSHOT_BYTES) {
      setScreenshotError(`Screenshot too large. Max ${MAX_SCREENSHOT_MB}MB.`);
      return;
    }
    setScreenshot(file);
  }

  async function handleSubmit() {
    setSubmitError(null);
    const trimmed = text.trim();
    if (!trimmed) {
      setSubmitError("Add a few words so I know what you mean.");
      return;
    }
    setSubmitting(true);
    try {
      await submitFeedback({
        text: trimmed,
        screenshot,
        pageUrl: context.pageUrl,
        conversationId: context.conversationId ?? undefined,
      });
      setSubmitted(true);
      setText("");
      setScreenshot(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
    } catch (err) {
      setSubmitError(
        err instanceof Error ? err.message : "Couldn't send feedback. Try again?",
      );
    } finally {
      setSubmitting(false);
    }
  }

  if (submitted) {
    return (
      <div className="max-w-2xl mx-auto px-4 md:px-8 py-8 space-y-6">
        <header className="flex items-center gap-3">
          <FeedbackIcon size={22} strokeWidth={ICON_STROKE} className="text-accent" />
          <h1 className="text-2xl font-bold text-text tracking-tight">Feedback</h1>
        </header>
        <div className="rounded-2xl border border-accent/30 bg-accent/[0.06] p-6 space-y-3">
          <div className="text-base font-semibold text-text">Got it, thanks.</div>
          <p className="text-sm text-text-muted leading-relaxed">
            Your feedback landed. I'll read every one of these.
          </p>
          <button
            type="button"
            onClick={() => setSubmitted(false)}
            className="text-sm text-accent hover:underline"
          >
            Send another →
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto px-4 md:px-8 py-8 space-y-6">
      <header className="flex items-center gap-3">
        <FeedbackIcon size={22} strokeWidth={ICON_STROKE} className="text-accent" />
        <div>
          <h1 className="text-2xl font-bold text-text tracking-tight">Feedback</h1>
          <p className="text-sm text-text-muted">
            Bugs, ideas, complaints: all welcome.
          </p>
        </div>
      </header>

      <section className="rounded-2xl border border-border bg-surface p-5 space-y-4">
        <label className="block">
          <span className="text-sm font-medium text-text">What's on your mind?</span>
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value.slice(0, MAX_TEXT))}
            rows={4}
            placeholder="The verdict on K-8-3 looked wrong because…"
            className="mt-2 block w-full rounded-lg border border-border bg-bg px-3 py-2 text-text placeholder:text-text-muted/60 focus:outline-none focus:border-accent/50 transition"
          />
          <div className="mt-1 text-right text-xs text-text-muted">
            {text.length} / {MAX_TEXT}
          </div>
        </label>

        <div>
          <span className="text-sm font-medium text-text">Screenshot (optional)</span>
          <p className="text-xs text-text-muted mt-1">
            Drag a screenshot, or pick a file. Max {MAX_SCREENSHOT_MB}MB. PNG / JPG / WEBP.
          </p>
          {screenshot ? (
            <div className="mt-2 flex items-center gap-3 rounded-lg border border-border bg-bg px-3 py-2">
              <div className="flex-1 min-w-0">
                <div className="text-sm text-text truncate">{screenshot.name}</div>
                <div className="text-xs text-text-muted">
                  {(screenshot.size / 1024).toFixed(0)} KB
                </div>
              </div>
              <button
                type="button"
                onClick={() => pickScreenshot(null)}
                className="text-text-muted hover:text-text transition p-1"
                aria-label="Remove screenshot"
              >
                <X size={16} strokeWidth={ICON_STROKE} />
              </button>
            </div>
          ) : (
            <input
              ref={fileInputRef}
              type="file"
              accept="image/png,image/jpeg,image/webp,image/heic,image/gif"
              onChange={(e) => pickScreenshot(e.target.files?.[0] ?? null)}
              className="mt-2 block w-full text-sm text-text-muted file:mr-3 file:py-2 file:px-3 file:rounded-lg file:border file:border-border file:bg-bg file:text-text file:text-sm file:font-medium hover:file:border-accent/40 file:cursor-pointer cursor-pointer"
            />
          )}
          {screenshotError && (
            <div className="mt-2 text-xs text-danger">{screenshotError}</div>
          )}
        </div>
      </section>

      <section className="rounded-2xl border border-border bg-surface p-4 space-y-2">
        <div className="text-[11px] uppercase tracking-[0.12em] font-semibold text-text-muted">
          Auto-attached context
        </div>
        <p className="text-xs text-text-muted leading-relaxed">
          So I can match your feedback to what you were looking at:
        </p>
        <dl className="text-xs space-y-1 font-mono">
          <div className="flex gap-2">
            <dt className="text-text-muted/70 shrink-0">URL:</dt>
            <dd className="text-text break-all">{context.pageUrl}</dd>
          </div>
          {context.conversationId && (
            <div className="flex gap-2">
              <dt className="text-text-muted/70 shrink-0">Convo:</dt>
              <dd className="text-text">{context.conversationId}</dd>
            </div>
          )}
        </dl>
      </section>

      {submitError && (
        <div className="rounded-lg border border-danger/50 bg-danger/10 px-4 py-3 text-sm text-danger">
          {submitError}
        </div>
      )}

      <button
        type="button"
        onClick={handleSubmit}
        disabled={submitting || !text.trim()}
        className="w-full px-4 py-3 rounded-lg bg-accent text-white text-sm font-semibold hover:bg-accent/90 transition disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {submitting ? "Sending…" : "Send feedback"}
      </button>
    </div>
  );
}
